import React, { useMemo } from 'react';

import { SWReactIcons } from '@similarweb/icons';

import { Modal } from '../../../components/Modal';
import { NewTag } from '../../../components/NewTag';
import { Preference } from '../../../domain/preferences';
import { IS_SAM_LANGUAGE_TOOLTIP_SHOWN, IS_SAM_PREFERENCES_TOOLTIP_SHOWN } from '../../../domain/user';
import { useIsOutOfEmailsQuota } from '../../../features/insights/useIsOutOfEmailsQuota';
import { useValuePropPreferencesQuery } from '../../../features/preferences/useValuePropPreferencesQuery';
import { useIsCountrySettingsAvailable } from '../../../features/user/useIsCountrySettingsAvailable';
import { useUserSettings } from '../../../features/user/useUserSettings';
import { translate } from '../../../services/i18n';
import { InsightsQuotaTooltip } from '../InsightsQuotaTooltip';

import { CountryPreferences } from './CountryPreferences';
import { LanguagePreferences } from './LanguagePreferences';
import { Metrics } from './Metrics';
import {
  StyledPreferencesButton,
  StyledPreferenceOption,
  StyledPreferenceOptionLabel,
  StyledPreferenceTooltip,
} from './styles';
import { usePreferences } from './usePreferences';
import { ValueProposition } from './ValueProposition';

const PREFERENCES = [
  { key: Preference.valueProposition, title: 'insights.preferences.value_prop.title', component: ValueProposition },
  { key: Preference.metrics, title: 'insights.preferences.metrics.title', component: Metrics },
  { key: Preference.countries, title: 'insights.preferences.countries.title', component: CountryPreferences },
  {
    key: Preference.language,
    title: 'insights.preferences.language.title',
    component: LanguagePreferences,
    isNew: true,
  },
];

export const Preferences = () => {
  const isCountriesSettingAvailable = useIsCountrySettingsAvailable();
  const { getUserSetting, setUserSettings } = useUserSettings();

  const isOutOfEmailsQuota = useIsOutOfEmailsQuota();
  const { custom } = useValuePropPreferencesQuery().data ?? {};
  const isMissingValueProp = !custom?.painPoint || !custom?.valueProposition;

  const {
    isModalOpen,
    handleOpenModal,
    handleCloseModal,
    isInnerModalOpen,
    innerModal,
    handleClickOnPreference,
    handleCloseInnerModal,
  } = usePreferences();

  const preferencesToShow = useMemo(() => {
    return isCountriesSettingAvailable
      ? PREFERENCES
      : PREFERENCES.filter((preference) => preference.key !== Preference.countries);
  }, [isCountriesSettingAvailable]);

  const tooltipProps = useMemo(() => {
    const createTooltipProps = ({
      content,
      onOutsideClick,
      minWidth = 200,
    }: {
      content: string;
      onOutsideClick?: () => void;
      minWidth?: number;
    }) => ({
      content,
      visible: true,
      withOutsideClick: true,
      onOutsideClick,
      enabled: true,
      minWidth,
    });

    if (isMissingValueProp) {
      return createTooltipProps({ content: translate('insights.preference.missing_vp') });
    }

    if (!getUserSetting(IS_SAM_PREFERENCES_TOOLTIP_SHOWN)) {
      return createTooltipProps({
        content: translate('insights.preference.tooltip'),
        onOutsideClick: () => setUserSettings({ [IS_SAM_PREFERENCES_TOOLTIP_SHOWN]: true }),
      });
    }

    if (!getUserSetting(IS_SAM_LANGUAGE_TOOLTIP_SHOWN)) {
      return createTooltipProps({
        content: translate('insights.preference.language.tooltip'),
        onOutsideClick: () => setUserSettings({ [IS_SAM_LANGUAGE_TOOLTIP_SHOWN]: true }),
        minWidth: 168,
      });
    }

    return {
      content: '',
      enabled: false,
      visible: false,
    };
  }, [isMissingValueProp]);

  const renderPreferencesButton = () => {
    return (
      <StyledPreferencesButton onClick={handleOpenModal} disabled={isOutOfEmailsQuota}>
        <SWReactIcons iconName="edit-icon" size="xs" />
        <span>{translate('insights.preferences.title')}</span>
      </StyledPreferencesButton>
    );
  };

  return (
    <>
      {isOutOfEmailsQuota ? (
        <InsightsQuotaTooltip minWidth={230} location="preferences">
          {renderPreferencesButton()}
        </InsightsQuotaTooltip>
      ) : (
        <StyledPreferenceTooltip {...tooltipProps}>{renderPreferencesButton()}</StyledPreferenceTooltip>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        heading={translate('insights.preferences.title')}
        subheading={translate('insights.preferences.subtitle')}
        contentHorizontalPadding={0}
      >
        {preferencesToShow.map(({ key, title, component: PreferenceComponent, isNew }) => (
          <React.Fragment key={key}>
            <StyledPreferenceOption
              onClick={() => handleClickOnPreference(key)}
              isSelected={innerModal === key}
              height={isInnerModalOpen ? '0' : 'auto'}
            >
              <StyledPreferenceOptionLabel>
                {translate(title)}
                {isNew ? <NewTag /> : null}
              </StyledPreferenceOptionLabel>
              <SWReactIcons iconName="chev-right" size="sm" />
            </StyledPreferenceOption>
            <PreferenceComponent
              isOpen={isInnerModalOpen ? innerModal === key : null}
              onCloseInnerModal={handleCloseInnerModal}
              onClose={handleCloseModal}
              isNew={isNew}
            />
          </React.Fragment>
        ))}
      </Modal>
    </>
  );
};
